import { process, State } from "@progress/kendo-data-query";
import React from "react";
import { DataLoadingContext } from "../../components/DataLoading";
import { ChangeLogType, DeletionLog } from "../../models/ChangeLog";
import { ChangeLogService, FeedbackService } from "../../api";
import { Grid, GridCellProps, GridColumn, GridDataStateChangeEvent, GridFilterCellProps, } from "@progress/kendo-react-grid";
import pagerSettings from "../../utils/pagerSettings";
import { formatDate, formatTime, getListFromEnum } from "../../utils";
import GridDropDownFilter from "../../components/GridDropDownFilter";
import { NotificationActionType, NotificationContext } from "../../components/Core/Notification/state";
import { ReactComponent as RestoreIcon } from '../../assets/icons/file copy.svg';
import { ReactComponent as ExternalLinkIcon } from '../../assets/icons/external-link.svg';
import IconButton from "../../components/Core/IconButton";
import { AlertDialogActionType, AlertDialogContext } from "../../components/Core/AlertDialog/state";
import { useHistory } from "react-router-dom";
import Feedback from "../../models/Feedback";
import Modal from "../../components/Core/Modal";
import NoteEditor from "../Dashboard/NoteEditor";
import UserModal from "../Users/UserModal";


const DeletionLogs: React.FC = () => {
    const [dataState, setDataState] = React.useState<State>({ skip: 0, take: 20 });
    const [feedback, setFeedback] = React.useState<Feedback | null>(null);
    const [showFeedback, setShowfeedback] = React.useState<boolean>(false);
    const [selectedUserId, setSelectedUserId] = React.useState<string | null>(null);
    const { toggleLoading } = React.useContext(DataLoadingContext);
    const notificationDispatch = React.useContext(NotificationContext).notificationDispatch;
    const [logs, setLogs] = React.useState<DeletionLog[]>([]);
    const history = useHistory();
    const alertDialogDispatch = React.useContext(AlertDialogContext).alertDialogDispatch;
    const getData = () => {
        toggleLoading(true);
        ChangeLogService.getDeletionLogs()
            .then((response) => {
                setLogs(response);
            })
            .finally(() => toggleLoading(false));
    };
    React.useEffect(() => {
        getData();
    }, []);

    const getFeedback = (id: string) => {
        FeedbackService.getFeedbackById(id).then((response) => {
            setFeedback(response);
        }).finally(() => {
            setShowfeedback(true);
        });
    }

    const processedLogs = React.useMemo(() => {
        if (!logs) return [];
        return logs.map(log => {

            return {
                ...log,
                dateOnly: formatDate(log.dateDeleted), // The original timestamp works for date sorting
                timeOnly: formatTime(log.dateDeleted),      // Use the numeric value for correct time sorting
            };
        });
    }, [logs]);

    const handleDataStateChange = (e: GridDataStateChangeEvent) => {
        setDataState(e.dataState);
    }
    const types = getListFromEnum(Object.values(ChangeLogType));

    const TypeFilterCell = (props: GridFilterCellProps) => (
        <GridDropDownFilter
            {...props}
            data={types}
            defaultValue={{ label: 'Select Type', value: '' }}
            filterOperator="eq"
        />
    );
    const TypeCell = (props: GridCellProps) => {
        return <td className='clickable-grid-custom-item'>{ChangeLogType[props.dataItem.type]}</td>;
    };

    const DateCell = (props: GridCellProps) => {
        return <td className='clickable-grid-custom-item'>{formatDate(props.dataItem.dateDeleted)}</td>;
    };
    const TimeCell = (props: GridCellProps) => {
        return <td className='clickable-grid-custom-item'>{formatTime(props.dataItem.dateDeleted)}</td>;
    };

    const handleRestoreFeedback = () => {
        toggleLoading(true);
        ChangeLogService.restoreItemByExternalId(feedback?.id as string, ChangeLogType.Feedback).then(() => {
            notificationDispatch({
                type: NotificationActionType.OPEN,
                payload: {
                    text: "successfully restored the feedback",
                    status: 'success',
                    autoClose: false,
                },
            });
            getData();
            setFeedback(null);
            setShowfeedback(false);
        }).catch((e) => {
            notificationDispatch({
                type: NotificationActionType.OPEN,
                payload: {
                    text: e.message,
                    status: 'error',
                    autoClose: true,
                },
            });
        }).finally(() => {
            toggleLoading(false);
        });
    }


    const handleRestore = (id: string) => {
        alertDialogDispatch({
            type: AlertDialogActionType.OPEN,
            payload: {
                title: 'Restore Item',
                description: `Are you sure you want to restore this item?`,
                handleConfirm: () => OnRestore(id),
            },
        });
    };
    const OnRestore = (id: string) => {
        toggleLoading(true);
        ChangeLogService.restoreItem(id).then((response) => {
            if (response) {
                getData();
                notificationDispatch({
                    type: NotificationActionType.OPEN,
                    payload: {
                        text: "Successfully restored the item",
                        status: 'success',
                        autoClose: false,
                    },
                });
            }
            else {
                notificationDispatch({
                    type: NotificationActionType.OPEN,
                    payload: {
                        text: "Something went wrong while restoring the item",
                        status: 'error',
                        autoClose: true,
                    },
                });
            }
        }
        ).catch((e) => {
            notificationDispatch({
                type: NotificationActionType.OPEN,
                payload: {
                    text: e.message,
                    status: 'error',
                    autoClose: false,
                },
            });
        }).finally(() => {
            toggleLoading(false);
        });
    }

    const GridActions = (props: GridCellProps) => {
        const data = props.dataItem as DeletionLog;
        return (
            <td className="grid-actions">
                <IconButton
                    Icon={ExternalLinkIcon}
                    color="info"
                    onClick={() => handleRowClick(data)}
                    tooltip='View Details'
                />
                <IconButton
                    Icon={RestoreIcon}
                    color={'success'}
                    onClick={() => {
                        handleRestore(data.id);
                    }}
                    tooltip='Restore'
                />
            </td>
        );
    };

    const handleRowClick = (dataItem: DeletionLog) => {
        switch (dataItem.type) {
            case ChangeLogType.Station:
                history.push(`/stations/${dataItem.externalId}`);
                break;
            case ChangeLogType.Client:
                history.push(`/clients/${dataItem.externalId}`);
                break;
            case ChangeLogType.Project:
                history.push(`/projects/${dataItem.externalId}`);
                break;
            case ChangeLogType.User:
                setSelectedUserId(dataItem.externalId);
                break;
            case ChangeLogType.Feedback:
                getFeedback(dataItem.externalId);
                break;
            default:
                return;
        }
    }

    return (
        <div className="logs">
            <div className="logs-list">
                <Grid
                    style={{
                        height: '100%',
                    }}
                    sortable={true}
                    pageable={pagerSettings}
                    filterable={true}
                    data={process(processedLogs, dataState)}
                    {...dataState}
                    onDataStateChange={(e: GridDataStateChangeEvent) => {
                        handleDataStateChange(e);
                    }}
                    // eslint-disable-next-line react/prop-types
                    onRowClick={(props) => handleRowClick(props.dataItem as DeletionLog)}
                >
                    <GridColumn field="name" title="Name" />
                    <GridColumn field="type" title="Type" cell={TypeCell} filterCell={TypeFilterCell} />
                    <GridColumn field="deletedByName" title="Deleted By" />
                    <GridColumn field="dateOnly" title="Date Deleted" cell={DateCell} />
                    <GridColumn field="timeOnly" title="Time Deleted" cell={TimeCell} filterable={false} />
                    <GridColumn cell={GridActions} sortable={false} filterable={false} />
                </Grid>
                <Modal
                    isOpen={showFeedback}
                    close={() => {
                        setFeedback(null);
                        setShowfeedback(false);
                        getData();
                    }}
                    title={'Restore feedback'}
                    size="large"
                >
                    <NoteEditor
                        feedback={feedback?.comment}
                        stationId={feedback?.stationId}
                        contractId={feedback?.contractId}
                        feedbackId={feedback?.id}
                        feedbackSentiment={feedback?.category}
                        feedbackGenre={feedback?.genre}
                        feedBackDate={feedback?.feedbackDate}
                        handleRestore={handleRestoreFeedback}
                        readonly={true}
                    />
                </Modal>
                <UserModal
                    isOpen={selectedUserId !== null}
                    close={() => {
                        setSelectedUserId(null);
                    }}
                    userId={selectedUserId}
                    getData={getData}
                />
            </div>
        </div>
    )
}

export default DeletionLogs;